"use server";

import { eq, lt } from "drizzle-orm";
import { redirect } from "next/navigation";
import { z } from "zod";
import { createSession, destroySession, verifyPassword } from "@/lib/auth";
import { getDb, schema } from "@/lib/db";
import { homeFor } from "@/lib/guards";

// Freno simple contra intentos repetidos (en memoria; basta para el entorno de pruebas).
const attempts = new Map<string, { n: number; first: number }>();
const WINDOW = 15 * 60_000;
const MAX = 6;
// Hash de relleno para que el tiempo de respuesta no delate si el usuario existe.
const DUMMY = "$argon2id$v=19$m=19456,t=2,p=1$c29tZXNhbHRzb21lc2FsdA$Zm9vYmFyZm9vYmFyZm9vYmFyZm9vYmFyZm9vYmFyZm8";

const schemaIn = z.object({ email: z.email().max(200), password: z.string().min(1).max(200) });

export type LoginState = { error?: string } | undefined;

export async function loginAction(_prev: LoginState, form: FormData): Promise<LoginState> {
  const parsed = schemaIn.safeParse({
    email: String(form.get("email") ?? "").trim().toLowerCase(),
    password: String(form.get("password") ?? ""),
  });
  if (!parsed.success) return { error: "Revisa el correo y la contraseña." };
  const { email, password } = parsed.data;

  const now = Date.now();
  const rec = attempts.get(email);
  if (rec && now - rec.first < WINDOW && rec.n >= MAX) return { error: "Demasiados intentos. Espera unos minutos." };

  const [row] = await getDb()
    .select({ user: schema.users, tenantStatus: schema.tenants.status })
    .from(schema.users)
    .innerJoin(schema.tenants, eq(schema.users.tenantId, schema.tenants.id))
    .where(eq(schema.users.email, email))
    .limit(1);
  const user = row?.user;
  const ok = await verifyPassword(user?.passwordHash ?? DUMMY, password).catch(() => false);
  // El estado de la cuenta solo se revela a quien conoce la contraseña.
  if (user && user.active && ok && row.tenantStatus === "suspended") return { error: "Esta cuenta está suspendida. Contacta con tu proveedor." };
  if (user && !user.active && ok) return { error: "Tu usuario está desactivado. Contacta a tu administrador." };
  if (!user || !user.active || !ok) {
    attempts.set(email, rec && now - rec.first < WINDOW ? { n: rec.n + 1, first: rec.first } : { n: 1, first: now });
    return { error: "Correo o contraseña incorrectos." };
  }
  attempts.delete(email);
  await getDb().delete(schema.sessions).where(lt(schema.sessions.expiresAt, new Date())); // limpieza de sesiones vencidas
  await createSession(user.id);
  redirect(homeFor(user.role));
}

export async function logoutAction() {
  await destroySession();
  redirect("/login");
}
